import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Printer, FileText } from 'lucide-react';
import { modules, getModuleIcon } from '../data/modules';

function renderPrintContent(content) {
  const lines = content.split('\n');
  const elements = [];
  let inTable = false;
  let tableHeaders = [];
  let tableRows = [];

  const processInline = (text) => {
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/`(.+?)`/g, '<code style="background:#fdf2f8;padding:1px 4px;border-radius:3px;font-size:0.85em;">$1</code>');
    return text;
  };

  const flushTable = () => {
    if (tableHeaders.length > 0) {
      elements.push(
        <table key={`t-${elements.length}`} className="qrc-table">
          <thead>
            <tr>{tableHeaders.map((h, i) => <th key={i}>{h.trim()}</th>)}</tr>
          </thead>
          <tbody>
            {tableRows.map((row, ri) => (
              <tr key={ri}>{row.map((c, ci) => <td key={ci} dangerouslySetInnerHTML={{ __html: processInline(c.trim()) }} />)}</tr>
            ))}
          </tbody>
        </table>
      );
    }
    tableHeaders = [];
    tableRows = [];
    inTable = false;
  };

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed.split('|').filter(c => c.trim() !== '');
      if (!inTable) { inTable = true; tableHeaders = cells; }
      else if (cells.every(c => /^[\s-:]+$/.test(c))) continue;
      else tableRows.push(cells);
      continue;
    } else if (inTable) flushTable();

    if (!trimmed) { elements.push(<div key={i} className="qrc-spacer" />); continue; }
    if (/^\d+\.\s/.test(trimmed)) {
      const num = trimmed.match(/^(\d+)/)[1];
      const text = trimmed.replace(/^\d+\.\s/, '');
      elements.push(<div key={i} className="qrc-numbered"><span className="qrc-num">{num}</span><span dangerouslySetInnerHTML={{ __html: processInline(text) }} /></div>);
      continue;
    }
    if (trimmed.startsWith('- ')) {
      elements.push(<div key={i} className="qrc-bullet"><span className="qrc-dot" /><span dangerouslySetInnerHTML={{ __html: processInline(trimmed.substring(2)) }} /></div>);
      continue;
    }
    elements.push(<p key={i} className="qrc-para" dangerouslySetInnerHTML={{ __html: processInline(trimmed) }} />);
  }
  if (inTable) flushTable();
  return elements;
}

export default function QuickRefPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const stepParam = searchParams.get('step');

  const module = modules.find(m => m.id === slug);
  if (!module) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-xl font-semibold text-slate-800">Module not found</h2>
        <Link to="/" className="text-[#00b1e8] text-sm mt-2 inline-block">Back to Home</Link>
      </div>
    );
  }

  const Icon = getModuleIcon(module.icon);
  const stepsToRender = stepParam !== null
    ? [module.steps[parseInt(stepParam, 10)]].filter(Boolean)
    : module.steps;
  const isSingleStep = stepParam !== null;

  const handlePrint = () => window.print();

  return (
    <div className="qrc-page" data-testid="quick-ref-page">
      {/* Screen-only toolbar */}
      <div className="qrc-toolbar no-print">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to={`/module/${slug}`} className="text-slate-400 hover:text-[#00b1e8] transition-colors" data-testid="qrc-back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div>
              <h1 className="font-['Outfit'] text-base font-bold text-slate-900">
                Quick Reference Card{!isSingleStep ? 's' : ''}
              </h1>
              <p className="text-xs text-slate-500">
                {module.title} {isSingleStep && stepsToRender[0] ? `— ${stepsToRender[0].title}` : `— ${stepsToRender.length} cards`}
              </p>
            </div>
          </div>
          <button
            data-testid="qrc-print-btn"
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#00b1e8] hover:bg-[#0091c2] text-white text-sm font-medium rounded-xl transition-colors"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
        </div>
      </div>

      {/* Printable Cards */}
      <div className="qrc-cards">
        {stepsToRender.map((step, idx) => {
          const stepNum = isSingleStep ? parseInt(stepParam, 10) + 1 : idx + 1;
          return (
            <div key={idx} className="qrc-card" data-testid={`qrc-card-${idx}`}>
              {/* Card Header */}
              <div className="qrc-header">
                <div className="qrc-header-left">
                  <div className="qrc-logo-mark" style={{ backgroundColor: module.color }}>
                    <span style={{ color: 'white', fontSize: '14px', fontWeight: 700 }}>K4</span>
                  </div>
                  <div>
                    <div className="qrc-brand">kestral</div>
                    <div className="qrc-brand-sub">Quick Reference Card</div>
                  </div>
                </div>
                <div className="qrc-header-right">
                  <div className="qrc-module-name" style={{ color: module.color }}>{module.title}</div>
                  <div className="qrc-step-label">Step {stepNum} of {module.steps.length}</div>
                </div>
              </div>

              {/* Separator */}
              <div className="qrc-separator" style={{ backgroundColor: module.color }} />

              {/* Card Title */}
              <h2 className="qrc-title">{step.title}</h2>

              {/* Card Content */}
              <div className="qrc-content">
                {renderPrintContent(step.content)}
              </div>

              {/* Card Footer */}
              <div className="qrc-footer">
                <span>Kestral Karisma K4 Training Guide</span>
                <span>{module.title} — Step {stepNum}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
